import React, { useState, useCallback, useRef } from 'react';
import { X, Link, Search, Loader2, Database } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useNotification } from '../../context/NotificationContext';

/**
 * ERP Product Sync Modal
 *
 * 搜索/选择产品管理（entity_product）中的产品，关联到渠道产品。
 * 在渠道产品列表页的操作栏触发。
 *
 * Props:
 *   entityId    - entity ID
 *   entityId    - entity_channel.entity_id
 *   sku         - 渠道产品 SKU
 *   productId   - 当前已关联的 entity_product.entity_id（可为 null）
 *   erpSku      - 当前已关联的 ERP 产品 SKU（可为空）
 *   onClose     - () => void
 *   onSuccess   - () => void
 */
const ErpProductSyncModal = ({ entityId, sku, productId, erpSku, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const inputRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(
    productId ? { entity_id: productId, sku: erpSku || '' } : null
  );
  const [submitting, setSubmitting] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = useCallback(async () => {
    const q = searchQuery.trim();
    if (!q && !searched) return;
    setSearching(true);
    setSearched(true);
    try {
      const url = `/api/channel/eav/products/erp-search?limit=20${q ? `&search=${encodeURIComponent(q)}` : ''}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.status === 'success') {
        setSearchResults(data.data || []);
      } else {
        setSearchResults([]);
      }
    } catch (e) {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [searchQuery, searched]);

  const handleSelect = (product) => {
    setSelectedProduct(prev => prev?.entity_id === product.entity_id ? null : product);
  };

  const handleSync = async () => {
    if (!selectedProduct) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/channel/eav/products/sync-erp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_id: entityId, product_id: selectedProduct.entity_id }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notify(t('common.success'), 'success');
        onSuccess && onSuccess();
      } else {
        notify(data.message || t('common.error'), 'error');
      }
    } catch (e) {
      notify(t('common.error'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const currentLinked = productId ? { entity_id: productId, sku: erpSku || '' } : null;

  return (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
            {t('channel.sync_erp_product') || 'Link ERP Product'}
          </h2>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Current channel product info */}
          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500 dark:text-slate-400">{t('channel.channel_sku')}</span>
              <span className="font-mono text-slate-800 dark:text-slate-200">{sku || '-'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500 dark:text-slate-400">{t('channel.entity_id')}</span>
              <span className="font-mono text-slate-800 dark:text-slate-200">{entityId || '-'}</span>
            </div>
          </div>

          {/* Currently linked product */}
          {currentLinked && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-1">
              <div className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                {t('channel.currently_linked') || 'Currently Linked'}
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-800 dark:text-slate-200">
                <Database size={14} className="text-blue-500" />
                <span className="font-mono font-semibold">#{currentLinked.entity_id}</span>
                {currentLinked.sku && (
                  <span className="text-slate-500 dark:text-slate-400">({currentLinked.sku})</span>
                )}
              </div>
            </div>
          )}

          {/* Search */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
              {t('channel.search_erp_product') || 'Search ERP Product'}
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setSearched(false); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                  placeholder={t('channel.search_by_sku') || 'Search by SKU...'}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200"
                />
              </div>
              <button onClick={handleSearch} disabled={searching}
                className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors inline-flex items-center gap-2">
                {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                {t('common.search')}
              </button>
            </div>
          </div>

          {/* Search results */}
          {searching ? (
            <div className="flex justify-center py-8">
              <Loader2 size={24} className="animate-spin text-slate-400" />
            </div>
          ) : searched && searchResults.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400 dark:text-slate-500">
              {t('channel.no_erp_products_found') || 'No ERP products found'}
            </div>
          ) : searchResults.length > 0 ? (
            <div className="max-h-60 overflow-y-auto space-y-1 border border-slate-200 dark:border-slate-600 rounded-lg">
              {searchResults.map(product => {
                const isSelected = selectedProduct?.entity_id === product.entity_id;
                const isCurrent = currentLinked?.entity_id === product.entity_id;
                return (
                  <button key={product.entity_id}
                    onClick={() => handleSelect(product)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors border-b last:border-b-0 border-slate-100 dark:border-slate-700 ${
                      isSelected
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : isCurrent
                          ? 'bg-blue-50/50 dark:bg-blue-900/10 text-slate-700 dark:text-slate-300'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-300'
                    }`}>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      isSelected
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-slate-300 dark:border-slate-500'
                    }`}>
                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-xs text-slate-400 dark:text-slate-500 shrink-0">
                        #{product.entity_id}
                      </span>
                      <span className="font-medium text-sm truncate">{product.sku || '-'}</span>
                      {isCurrent && (
                        <span className="text-xs text-blue-500 dark:text-blue-400 ml-auto shrink-0">
                          ({t('channel.currently_linked_short') || 'linked'})
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : null}

          {/* Selected product confirmation */}
          {selectedProduct && (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide mb-1">
                {t('channel.selected_erp_product') || 'Selected ERP Product'}
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-800 dark:text-slate-200">
                <Link size={14} className="text-green-500" />
                <span className="font-mono font-semibold">#{selectedProduct.entity_id}</span>
                {selectedProduct.sku && (
                  <span className="text-slate-500 dark:text-slate-400">({selectedProduct.sku})</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            {t('common.cancel')}
          </button>
          <button onClick={handleSync} disabled={!selectedProduct || submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors inline-flex items-center gap-2">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Link size={14} />}
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErpProductSyncModal;
